<script setup lang="ts">

import type { ContractData } from '@/contracts/types/types';
import { PaymentTypeOptions } from '@/contracts/types/types';
import { ElAlert } from 'element-plus';
import { ElForm, ElFormItem, ElInput, ElOption, ElSelect } from 'element-plus';
import { vMaska } from 'maska/vue';
import MoneyInput from '@/application/components/form-elements/MoneyInput/MoneyInput.vue';
import DateInput from '@/application/components/form-elements/DateInput/DateInput.vue';
import { toOptions } from '@/application/composables/vselect';
import { useGesellschaften } from '@/application/composables/gesellschaften';
import { useDetectMobile } from '@/application/composables/detectMobile';
import { useSparten } from '@/documents/composables/sparten';
import { computed } from 'vue';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';
import { storeToRefs } from 'pinia';
import { useAiDocumentExtractionStore } from '@/documents/store/aiDocumentExtractionStore.ts';
import AiIndicator from '@/application/components/AiIndicator.vue';
import { isBefore, isEqual, differenceInDays, parse } from 'date-fns';

interface Props {
  modelValue: ContractData,
  updateField: () => void,
  validationErrors?: Record<string, string[]>,
  updateForm?: boolean,
}

const props = defineProps<Props>();

const { isMobile } = useDetectMobile();
const { sparten, fetchSparten } = useSparten();
const { gesellschaften, fetchGesellschaften } = useGesellschaften();
const { duzen } = useBaseDataStore();
const { extract } = storeToRefs(useAiDocumentExtractionStore());

const { isKfzSparte } = useGesellschaften();
const isKfz = computed(() => isKfzSparte(props.modelValue.sparte_id));
const gesellschaftOptions = toOptions(gesellschaften, 'name', 'id');

// The Kennzeichen is a single inline row of inputs, so collect the per-part validation
// errors (kennzeichen.unterscheidungszeichen, .erkennungszeichen, ...) under one key that
// the Kennzeichen form group can display.
const validationErrorsWithKennzeichen = computed<Record<string, string[]>>(() => {
  const errors = { ...(props.validationErrors ?? {}) };
  const kennzeichenErrors = [...new Set(
    Object.entries(errors)
      .filter(([key]) => key.startsWith('kennzeichen.'))
      .flatMap(([, messages]) => messages),
  )];

  if (kennzeichenErrors.length > 0) {
    errors.kennzeichen = kennzeichenErrors;
  }

  return errors;
});

fetchGesellschaften();
fetchSparten();

const dateWarning = computed(() => {
  const { beginn, ablauf } = extract.value;
  if (!beginn && !ablauf) return false; // if one is not set by AI response data, we don't show a warning

  const beginnDate = parse(props.modelValue.beginn ?? '', 'dd.MM.yyyy', new Date());
  const ablaufDate = parse(props.modelValue.ablaufdatum ?? '', 'dd.MM.yyyy', new Date());

  if (!beginnDate || !ablaufDate) return false;
  if (isBefore(ablaufDate, beginnDate)) return 'Das Ablaufdatum liegt vor dem Beginn.';
  if (isEqual(beginnDate, ablaufDate)) return 'Beginn und Ablaufdatum sind gleich. Wenn dies gewollt ist, einfach fortfahren.';
  if (Math.abs(differenceInDays(beginnDate, ablaufDate)) < 30) return 'Beginn und Ablaufdatum sind weniger als 30 Tage entfernt. Wenn dies gewollt ist, einfach fortfahren.';

  return false;
})
</script>

<template>
  <ElForm require-asterisk-position="right">
    <div class="flex flex-col gap-4">
      <ElFormItem
        for="Gesellschaft"
        :label-position="isMobile ? 'top' : 'left'"
        :error="validationErrorsWithKennzeichen.gesellschaft_id?.[0]"
      >
        <template #label>
          Gesellschaft
          <AiIndicator v-if="extract.gesellschaftId && extract.gesellschaftId === modelValue.gesellschaft_id && !updateForm" />
        </template>

        <div class="w-full flex justify-end">
          <ElSelect
            id="Gesellschaft"
            :model-value="modelValue.gesellschaft_id"
            :loading="gesellschaftOptions.length === 0"
            placeholder=""
            clearable
            class="w-full md:w-80"
            @update:model-value="updateField('gesellschaft_id', $event)"
          >
            <ElOption
              v-for="option in gesellschaftOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </ElSelect>
        </div>
      </ElFormItem>

      <ElFormItem
        for="sparte_id"
        :label-position="isMobile ? 'top' : 'left'"
        :error="validationErrorsWithKennzeichen.sparte_id?.[0]"
      >
        <template #label>
          Sparte
          <AiIndicator v-if="extract.sparteId && extract.sparteId === modelValue.sparte_id && !updateForm" />
        </template>

        <div class="w-full flex justify-end">
          <ElSelect
            id="sparte_id"
            :model-value="modelValue.sparte_id"
            placeholder=""
            clearable
            class="w-full md:w-80"
            @update:model-value="updateField('sparte_id', $event)"
          >
            <ElOption
              v-for="option in sparten"
              :key="option.id"
              :label="option.display_name"
              :value="option.id"
            >
              <span class="text-gray-800">{{ option.display_name }}</span>
              <span
                v-if="!!option.type"
                class="badge text-secondary"
              >
                {{ option.type }}
              </span>
            </ElOption>
          </ElSelect>
        </div>
      </ElFormItem>

      <ElFormItem
        for="vertragsnummer"
        :label-position="isMobile ? 'top' : 'left'"
        class="whitespace-nowrap"
        :error="validationErrorsWithKennzeichen.vertragsnummer?.[0]"
      >
        <template #label>
          Vertrags-Nr.
          <AiIndicator v-if="extract.vertragsnummer && extract.vertragsnummer === modelValue.vertragsnummer && !updateForm" />
        </template>

        <div class="w-full flex justify-end">
          <ElInput
            id="vertragsnummer"
            :model-value="modelValue.vertragsnummer"
            placeholder=""
            class="w-full md:w-80"
            @update:model-value="updateField('vertragsnummer', $event)"
          />
        </div>
      </ElFormItem>

      <ElFormItem
        for="beginn"
        :label-position="isMobile ? 'top' : 'left'"
        :error="validationErrorsWithKennzeichen.beginn?.[0]"
      >
        <template #label>
          Beginn
          <AiIndicator v-if="extract.beginn && extract.beginn === modelValue.beginn && !updateForm" />
        </template>

        <div class="w-full md:w-80 md:ml-auto">
          <DateInput
            id="beginn"
            :model-value="modelValue.beginn"
            placeholder=""
            @update:model-value="updateField('beginn', $event)"
          />
        </div>
      </ElFormItem>

      <ElFormItem
        for="ablaufdatum"
        :label-position="isMobile ? 'top' : 'left'"
        :error="validationErrorsWithKennzeichen.ablaufdatum?.[0]"
      >
        <template #label>
          Ablaufdatum
          <AiIndicator v-if="extract.ablauf && extract.ablauf === modelValue.ablaufdatum && !updateForm" />
        </template>

        <div class="w-full md:w-80 md:ml-auto">
          <DateInput
            id="ablaufdatum"
            :model-value="modelValue.ablaufdatum"
            @update:model-value="updateField('ablaufdatum', $event)"
          />
        </div>
      </ElFormItem>

      <ElAlert v-if="dateWarning" type="warning" show-icon>{{ dateWarning }}</ElAlert>

      <ElFormItem
        for="Beitrag"
        :label-position="isMobile ? 'top' : 'left'"
        :error="validationErrorsWithKennzeichen.beitrag?.[0]"
      >
        <template #label>
          Beitrag
          <AiIndicator v-if="extract.beitrag && extract.beitrag === modelValue.beitrag && !updateForm" />
        </template>

        <div class="w-full flex justify-end">
          <MoneyInput
            id="Beitrag"
            :model-value="modelValue.beitrag"
            class="w-full md:w-80"
            :disabled="updateForm"
            @update:model-value="updateField('beitrag', $event)"
          />
        </div>
      </ElFormItem>

      <ElFormItem
        for="Zahlweise"
        :label-position="isMobile ? 'top' : 'left'"
        :error="validationErrorsWithKennzeichen.zahlweise?.[0]"
      >
        <template #label>
          Zahlweise
          <AiIndicator v-if="extract.zahlweise && extract.zahlweise === modelValue.zahlweise && !updateForm" />
        </template>

        <div class="w-full flex justify-end">
          <ElSelect
            id="Zahlweise"
            :model-value="modelValue.zahlweise"
            placeholder=""
            clearable
            class="w-full md:w-80"
            @update:model-value="updateField('zahlweise', $event)"
          >
            <ElOption
              v-for="option in PaymentTypeOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </ElSelect>
        </div>
      </ElFormItem>

      <ElFormItem
        v-if="isKfz"
        label="Kennzeichen"
        :label-position="isMobile ? 'top' : 'left'"
        :error="validationErrorsWithKennzeichen.kennzeichen?.[0]"
      >
        <div class="flex justify-between md:justify-end mx-auto lg:mx-0 w-full gap-1">
          <ElInput
            v-maska="{
              mask: 'AAA',
              tokens: { A: { pattern: /[a-zA-ZäöüÄÖÜ]/, uppercase: true } },
            }"
            :model-value="modelValue.kennzeichen.unterscheidungszeichen"
            :disabled="updateForm"
            style="max-width: 74px"
            @update:model-value="updateField('kennzeichen.unterscheidungszeichen', $event)"
          />
          <ElInput
            v-maska="{
              mask: 'AA',
              tokens: { A: { pattern: /[a-zA-ZäöüÄÖÜ]/, uppercase: true } },
            }"
            :model-value="modelValue.kennzeichen.erkennungszeichen"
            :disabled="updateForm"
            style="max-width: 56px"
            @update:model-value="updateField('kennzeichen.erkennungszeichen', $event)"
          />
          <ElInput
            v-maska="'####'"
            :model-value="modelValue.kennzeichen.erkennungsnummer"
            :disabled="updateForm"
            style="max-width: 78px"
            @update:model-value="updateField('kennzeichen.erkennungsnummer', $event)"
          />
          <ElSelect
            :model-value="modelValue.kennzeichen.abschliessendeszeichen"
            :disabled="updateForm"
            style="min-width: 100px"
            placeholder=""
            @update:model-value="updateField('kennzeichen.abschliessendeszeichen', $event)"
          >
            <ElOption label="H" value="H" />
            <ElOption label="E" value="E" />
          </ElSelect>
        </div>
      </ElFormItem>

      <ElFormItem
        v-if="!updateForm"
        label="Notiz für den Makler"
        for="notiz"
        :label-position="isMobile ? 'top' : 'left'"
      >
        <div class="w-full md:flex md:justify-end">
          <ElInput
            id="notiz"
            type="textarea"
            :model-value="modelValue.notiz"
            :placeholder="`${duzen('Hinterlassen Sie', 'Hinterlasse')} eine Notiz zur Vertragsanlage ...`"
            :rows="3"
            class="w-full md:w-80 md:ml-auto"
            @update:model-value="updateField('notiz', $event)"
          />
        </div>
      </ElFormItem>
    </div>

    <ElAlert
      v-if="isKfz && updateForm"
      type="info"
      class="mt-5 text-left el-alert--primary"
      show-icon
    >
      Kennzeichen können nicht bearbeitet werden. Bei Fehlern im Kennzeichen, {{ duzen('lösche', 'löschen Sie') }}
      den Vertrag und {{ duzen('lege', 'legen Sie') }} ihn neu an oder
      {{ duzen('frage deinen', 'fragen Sie Ihren') }} Makler.
    </ElAlert>
  </ElForm>
</template>
